import {useEffect, useMemo} from 'react';
import type {LoadStatus} from 'api/types';
import type {FilterSet} from 'services/advancedSearch/types';
import {
  useCreateFilterSetMutation,
  useDeleteFilterSetMutation,
  useGetFilterSetsQuery,
  useUpdateFilterSetMutation,
} from 'services/advancedSearch/advancedSearchApi';
import {mapRtkErrorToHttpError, queryStatusToLoadStatus} from 'store/api';
import {useSnacks} from 'components/snacks';
import type {FilterSetsType} from '../types';

export const useFilterSets = (): FilterSetsType => {
  const {errorSnack} = useSnacks();
  const {data, status: loadingStatus} = useGetFilterSetsQuery();
  const [createFilterSet, {status: creatingStatus, error: creatingError}] =
    useCreateFilterSetMutation();
  const [deleteFilterSet, {status: deletingStatus, error: deletingError}] =
    useDeleteFilterSetMutation();
  const [updateFilterSet, {status: updatingStatus, error: updatingError}] =
    useUpdateFilterSetMutation();

  useEffect(() => {
    if (creatingError) {
      errorSnack(
        'Creating filter set failed',
        mapRtkErrorToHttpError(creatingError),
      );
    }

    if (updatingError) {
      errorSnack(
        'Updating filter set failed',
        mapRtkErrorToHttpError(updatingError),
      );
    }

    if (deletingError) {
      errorSnack(
        'Deleting filter set failed',
        mapRtkErrorToHttpError(deletingError),
      );
    }
  }, [creatingError, updatingError, deletingError, errorSnack]);

  const status: LoadStatus = useMemo(() => {
    if (
      creatingStatus === 'rejected' ||
      updatingStatus === 'rejected' ||
      deletingStatus === 'rejected'
    ) {
      return 'Failed';
    }

    if (
      creatingStatus === 'pending' ||
      updatingStatus === 'pending' ||
      deletingStatus === 'pending'
    ) {
      return 'Loading';
    }

    return queryStatusToLoadStatus(loadingStatus);
  }, [creatingStatus, updatingStatus, deletingStatus, loadingStatus]);

  return useMemo(() => {
    const onCreate = async (filterSet: Omit<FilterSet, 'id'>) => {
      await createFilterSet(filterSet);
    };
    const onEdit = async (filterSet: FilterSet) => {
      await updateFilterSet(filterSet);
    };
    const onDelete = async (filterSet: Pick<FilterSet, 'id'>) => {
      await deleteFilterSet(filterSet);
    };

    return {
      filterSets: data ?? [],
      statusFilterSets: status,
      createFilterSet: onCreate,
      editFilterSet: onEdit,
      deleteFilterSet: onDelete,
    };
  }, [createFilterSet, updateFilterSet, data, deleteFilterSet, status]);
};
